/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useState } from "react";
import { InnerVerticalTabs } from "../../../../components/Tabs/InnerVerticalTabs/InnerVerticalTabs";
import Content from "./Content";
import { BooleanParam, StringParam, useQueryParam, withDefault } from "use-query-params";
import { ArgosFavoriteIconLogic } from "../../../../components/Buttons/ArgosFavoriteIconLogic";
import { useReadWriteFavorites } from "../../../../../api/helper";
import { FAVORITE_PARAM_ID, FAVORITE_TYPE, INNERVTAB_URL_PARAM } from "../constants";
import { useAppSelector } from "../../../../../redux/hooks";
import { selectGlobalDialogState } from "../../../../../redux/ducks/GlobalDialog/slice";
import { useSearchParams } from "react-router-dom";
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import { Ultrasonic } from "../../constants";
import { ObjectDef } from "../../../objectApp/constants";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";


export const ViewPublicConfigsParam = withDefault(BooleanParam, false)

interface UltrasonicPerformanceProps {
  sensor: Ultrasonic
  objects: ObjectDef[]
  gmid: string
  onSaveConfiguration: any
  onDeleteConfiguration: any
}

const UltrasonicPerformance = (props: UltrasonicPerformanceProps) => {

  const [tabs, setTabs] = useState<any>([])
  const [viewPublicConfigurations, setViewPublicConfigurations] = useQueryParam('viewPublicConfigs', ViewPublicConfigsParam)
  const [_index, fff] = useQueryParam(FAVORITE_PARAM_ID, StringParam)

  function getBaselineObject(tempToObjMap: any) {
    const tempValue = Object.keys(tempToObjMap)[0]; // Get the first temperature value as default
    const _newObjectName = tempToObjMap?.[tempValue]?.["objectName"];
    const firingMode = tempToObjMap?.[tempValue]?.["firingMode"];
    const version = tempToObjMap?.[tempValue]?.["objectVersion"];
    const newObj = {
      "objectCategory": tempToObjMap?.[tempValue]?.["objectCategory"],
      "objectName": _newObjectName,
      "objectVersion": version,
      objectVariant: tempToObjMap?.[tempValue]?.["objectVariant"],
      "firingMode": firingMode,
    };
    return newObj;
  }

  const getTemperatureToObjectMapping = () => {
    const sensorData: any = props.sensor?.plotData;
    const baselineData = props.sensor?.baseline;
    const tempToObjMap1: any = {};

    Object.keys(sensorData)?.forEach((detectionTable: any) => {
      if (Object.keys(sensorData[detectionTable]).length !== 0) {
        const tableData = sensorData?.[detectionTable]?.data;
        const tempKey: any = Object.keys(tableData)?.filter((key: any) => key.includes("temperature") && key.includes("value"))[0];
        const firingModeKey: any = Object.keys(tableData)?.filter((key: any) => key.includes("firingmode") && key.includes("value"))[0]

        const tempValueAsString: string = String(tableData[tempKey]);

        if (detectionTable === "Detection_Range_1" || detectionTable === "Detection_Range_2") {
          tempToObjMap1[tempValueAsString] = {
            "objectName": baselineData?.[0]?.objectName,
            "objectCategory": baselineData?.[0]?.objectCategory,
            "objectVariant": baselineData?.[0]?.objectVariant,
            objectVersion: baselineData?.[0]?.objectVersion,
            firingMode: tableData[firingModeKey],
          };
        }
      }
    });
    return {tempToObjMap1};
  };

  useEffect(() => {
    const objectData = props.objects
    const sensorUltrasonicData = props.sensor
    const sensorUltrasonicConfigs = sensorUltrasonicData.configurations

    const {tempToObjMap1} = getTemperatureToObjectMapping();
    const baselineObject1 = getBaselineObject(tempToObjMap1)

    sensorUltrasonicData["firingMode"] = baselineObject1.firingMode

    let localTabs: any[] = []

    // Baseline configuration
    const baselineData1 = {
      "sensorId": sensorUltrasonicData.id,
      "objectCategory": baselineObject1?.objectCategory,
      "objectName": baselineObject1?.objectName,
      "firingMode" : baselineObject1.firingMode,
      "objectVersion": baselineObject1.objectVersion,
      objectVariant: baselineObject1?.objectVariant,
      'temperature': Object.keys(tempToObjMap1)[0],
      'azimuthFOV': sensorUltrasonicData.azimuthFOV,
      'maxInstrumentedRange': sensorUltrasonicData.maxInstrumentedRange,
      'isPublicConfig': true,
    };
    const baselineTab = {
      "trackedItemUid": 'baseline',
      "name": "Baseline",
      "content": <Content configIndex={0} baselineData={baselineData1} objectData={objectData}
                           tempToObjMap={tempToObjMap1}
        sensorUltrasonicData={sensorUltrasonicData} onSaveConfiguration={props.onSaveConfiguration}
        onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid} />
    }
    localTabs.push(baselineTab)

    // Owner configurations
    const ownerConfigTabs = sensorUltrasonicConfigs?.filter((x: object) => x["owner" as keyof object] == props.gmid)
    const configTabs = ownerConfigTabs?.map((singleConfig: any, index: number) => ({
      "trackedItemUid": singleConfig['trackedItemUid'] ?? `item-${index}-${singleConfig['configName']}`,
      "name": singleConfig['configName'],
      "content": <Content configIndex={index + 1} baselineData={singleConfig} objectData={objectData}
                          tempToObjMap={tempToObjMap1}
        sensorUltrasonicData={sensorUltrasonicData}
        onSaveConfiguration={props.onSaveConfiguration}
        onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid} />
    }))
    if (configTabs != null) {
      localTabs = localTabs.concat(configTabs)
    }

    // Public configurations
    if (viewPublicConfigurations) {
      const publicConfigs = sensorUltrasonicConfigs?.filter((x: object) => x["isPublicConfig" as keyof object] && x["owner" as keyof object] != props.gmid)
      const publicConfigTabs = publicConfigs?.map((singleConfig: any, index: number) => ({
        "trackedItemUid": singleConfig['trackedItemUid'] ?? `public-item-${index}-${singleConfig['configName']}`,
        "name": singleConfig['configName'],
        "content": <Content configIndex={index + 1} baselineData={singleConfig} objectData={objectData}
                             tempToObjMap={tempToObjMap1}
          sensorUltrasonicData={sensorUltrasonicData}
          onSaveConfiguration={props.onSaveConfiguration}
          onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid} />
      }))
      if (publicConfigTabs != null) {
        localTabs = localTabs.concat(publicConfigTabs)
      }
    }

    setTabs(localTabs)
  }, [props.sensor, props.objects, viewPublicConfigurations, props.gmid, props.onDeleteConfiguration, props.onSaveConfiguration])

  return (
      <div style={{ flexGrow: 1, display: 'flex', height: '100%', flexDirection: "column" }}>
        <div style={{ display: 'flex', width: '100%' }}>
          <div style={{ marginLeft: 'auto' }}>
            <ArgosFavoriteIconLogic favoriteType={FAVORITE_TYPE} trackedItemUid={props?.sensor?.id} partitionKey={props?.sensor?.id}/>
          </div>
        </div>
        <InnerVerticalTabs tabs={tabs} favoriteType={FAVORITE_TYPE} uid={INNERVTAB_URL_PARAM}
        // onTabSelected={(index: number) => setSelectedTab(index)}
        />
      </div>
  )
}
export default UltrasonicPerformance;